import React, { useState, useRef, useEffect } from "react";
import { Form, Badge, Button, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faTimes, faCheck, faSearch } from "@fortawesome/free-solid-svg-icons";

const MultiSelectDropdown = ({ 
    label, 
    options = [], 
    selectedValues = [], 
    onChange, 
    placeholder = "Select options...",
    disabled = false,
    helpText = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm(""); // Clear search when closing
            }
        };

        const handleKeyDown = (event) => {
            if (isOpen && event.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                // Focus search input when opening
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 100);
            }
        }
    };

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        const label = typeof option === 'string' ? option : option.label;
        return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleOptionClick = (value) => {
        let newSelectedValues;
        if (selectedValues.includes(value)) {
            newSelectedValues = selectedValues.filter(v => v !== value);
        } else {
            newSelectedValues = [...selectedValues, value];
        }
        onChange(newSelectedValues);
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const handleSelectAll = () => {
        const allValues = filteredOptions.map(option => 
            typeof option === 'string' ? option : option.value
        );
        // Merge with existing selections from non-filtered options
        const existingValues = selectedValues.filter(value => {
            const option = options.find(opt => 
                (typeof opt === 'string' ? opt : opt.value) === value
            );
            if (!option) return false;
            const label = typeof option === 'string' ? option : option.label;
            return !label.toLowerCase().includes(searchTerm.toLowerCase());
        });
        onChange([...new Set([...existingValues, ...allValues])]);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Function to highlight search term in option labels
    const highlightSearchTerm = (text, searchTerm) => {
        if (!searchTerm) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => 
            regex.test(part) ? (
                <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
                    {part}
                </span>
            ) : part
        );
    };

    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return placeholder;
        } else if (selectedValues.length === 1) {
            const option = options.find(opt => 
                (typeof opt === 'string' ? opt : opt.value) === selectedValues[0]
            );
            return typeof option === 'string' ? option : option.label;
        } else {
            return `${selectedValues.length} selected`;
        }
    };

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div className="position-relative" ref={dropdownRef}>
                <div
                    className={`form-control d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''}`}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '38px' }}
                    onClick={handleToggle}
                >
                    <span className={selectedValues.length === 0 ? 'text-muted' : ''}>
                        {getDisplayText()}
                    </span>
                    <FontAwesomeIcon 
                        icon={isOpen ? faChevronUp : faChevronDown} 
                        className="text-muted"
                    />
                </div>
                
                {isOpen && !disabled && (
                    <div 
                        className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                        style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
                    >
                        {/* Search Input */}
                        <div className="p-2 border-bottom bg-light" style={{ position: 'sticky', top: 0, zIndex: 1001 }}>
                            <InputGroup size="sm">
                                <InputGroup.Text>
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputGroup.Text>
                                <Form.Control
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search options..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleClearSearch}
                                        style={{ padding: '0.25rem 0.5rem' }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                )}
                            </InputGroup>
                        </div>

                        {/* Action Buttons */}
                        {filteredOptions.length > 0 && (
                            <div className="p-2 border-bottom bg-light">
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleSelectAll}
                                        disabled={filteredOptions.every(option => {
                                            const value = typeof option === 'string' ? option : option.value;
                                            return selectedValues.includes(value);
                                        })}
                                    >
                                        Select All {searchTerm && 'Filtered'}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleClearAll}
                                        disabled={selectedValues.length === 0}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                                {searchTerm && (
                                    <div className="mt-1">
                                        <small className="text-muted">
                                            Showing {filteredOptions.length} of {options.length} options
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Options List */}
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-muted text-center">
                                {searchTerm ? `No options found for "${searchTerm}"` : "No options available"}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const value = typeof option === 'string' ? option : option.value;
                                const label = typeof option === 'string' ? option : option.label;
                                const isSelected = selectedValues.includes(value);
                                
                                return (
                                    <div
                                        key={index}
                                        className={`p-2 border-bottom cursor-pointer d-flex justify-content-between align-items-center ${isSelected ? 'bg-primary bg-opacity-10' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleOptionClick(value)}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = isSelected ? 'rgba(13, 110, 253, 0.1)' : 'transparent'}
                                    >
                                        <span>{highlightSearchTerm(label, searchTerm)}</span>
                                        {isSelected && (
                                            <FontAwesomeIcon 
                                                icon={faCheck} 
                                                className="text-primary"
                                            />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
            
            {/* Selected items display */}
            {selectedValues.length > 0 && (
                <div className="mt-2">
                    {selectedValues.map((value, index) => {
                        const option = options.find(opt => 
                            (typeof opt === 'string' ? opt : opt.value) === value
                        );
                        const label = typeof option === 'string' ? option : option?.label || value;
                        
                        return (
                            <Badge 
                                key={index} 
                                bg="primary" 
                                className="me-1 mb-1"
                                style={{ fontSize: '0.875rem' }}
                            >
                                {label}
                                <button
                                    type="button"
                                    className="btn-close btn-close-white ms-1"
                                    style={{ fontSize: '0.5rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOptionClick(value);
                                    }}
                                ></button>
                            </Badge>
                        );
                    })}
                </div>
            )}
            
            {helpText && (
                <Form.Text className="text-muted">
                    {helpText}
                </Form.Text>
            )}
        </Form.Group>
    );
};

export default MultiSelectDropdown; 